angular.module('storefrontApp')
    .service('dialogService', ['$uibModal', function ($uibModal) {
        return {
            showDialog(dialogData, controller, templateUrl, onClosed) {
                return $uibModal.open({
                    controller,
                    templateUrl,
                    resolve: {
                        dialogData() {
                            return dialogData;
                        },
                    },
                })
                    .result
                    .then(function (result) {
                        if (onClosed) {
                            onClosed(result);
                        }

                        return result;
                    });
            },
        };
    }])

    .controller('confirmDialogController', [
        '$scope',
        '$window',
        '$uibModalInstance',
        'dialogData',
        function (
            $scope,
            $window,
            $uibModalInstance,
            dialogData,
        ) {
            $scope.dialogData = dialogData;

            $scope.yes = function () {
                $uibModalInstance.close();
                $scope.dialogData.result = 'yes';
            };

            $scope.no = function () {
                $uibModalInstance.close();
                $scope.dialogData.result = 'no';
            };

            $scope.cancel = function () {
                $uibModalInstance.close();
                $scope.dialogData.result = 'cancel';
            };
        }])

    .service('confirmDialogService', ['$uibModal', function ($uibModal) {
        return {
            confirm(message, onClosed) {
                const dialogData = {
                    confirmMessage: message,
                };

                return $uibModal.open({
                    controller: 'confirmDialogController',
                    templateUrl: 'themes/assets/js/confirm-dialog.tpl.html',
                    resolve: {
                        dialogData() {
                            return dialogData;
                        },
                    },
                })
                    .result
                    .then(function () {
                        if (onClosed) {
                            onClosed(dialogData.result === 'yes');
                        }
                    });
            },
        };
    }])

    .service('feedbackService', ['$http', function ($http) {
        return {
            postFeedback(data) {
                return $http.post('storefrontapi/feedback', data);
            },
        };
    }])

    // Moves keyboard focus to a region after a dynamic content swap (WCAG 2.4.3).
    // Reusable pattern for any in-page region replacement (checkout steps, panels,
    // dialogs): pass the target element id (or the element). Defers one digest so
    // ng-if/ng-show content is in the DOM, makes the region programmatically
    // focusable, and moves focus so the browser scrolls it into view.
    .service('focusService', ['$timeout', '$document', function ($timeout, $document) {
        return {
            focusRegion(target) {
                $timeout(function () {
                    const el = angular.isString(target)
                        ? $document[0].getElementById(target)
                        : target;
                    if (!el) {
                        return;
                    }
                    if (!el.hasAttribute('tabindex')) {
                        el.setAttribute('tabindex', '-1');
                    }
                    el.focus();
                });
            },
        };
    }])

    // WCAG 4.1.3: the busy overlay is a status change presented visually and
    // nowhere else. Measured on QA 2026-09-15 while the checkout overlay was up:
    // role="status" 0, aria-busy 0, and the overlay itself carries no text at all
    // (templates/cart.liquid:4) -- so there is nothing for a screen reader to
    // read even if it were marked up. This holds the text; the live region that
    // speaks it is rendered once per layout, permanently, because a live region
    // inserted TOGETHER with its text announces nothing (the same lesson already
    // written up at templates/cart.liquid:257).
    //
    // Published on $rootScope so the region can live in the layout without
    // depending on which controller happens to own that part of the page.
    .service('busyService', ['$rootScope', '$timeout', function ($rootScope, $timeout) {
        // How long an operation has to run before it is worth announcing. A cart
        // update that resolves in 200ms is not a wait anybody sits through, and a
        // polite region that speaks on every one of them is chatter users switch
        // off -- which costs more than saying nothing.
        const ANNOUNCE_AFTER_MS = 300;

        // How long a busy state may hold the page before the block is lifted.
        // This is NOT a cancel: the operation may still be in flight, and
        // re-enabling "Create order" while an order is actually processing is
        // how duplicates happen ($checkout.js:406 sets creatingOrder and only
        // .finally clears it, and /api/paymentcallback has no idempotency). Only
        // the TRAP is released -- checkout.loading and every ng-disabled stay
        // exactly as they are, so the customer can reach the chat widget or
        // navigate away but cannot re-fire anything.
        const RELEASE_BLOCK_AFTER_MS = 20000;

        let depth = 0;
        let pending = null;
        let stuckTimer = null;

        const state = {
            isBusy: false,
            // Whether the page should be held inert. Watched by rsBusyBlock.
            blocked: false,
            // The block was lifted on the timeout above while the operation was
            // still running.
            stuck: false,
            // Translation key, rendered through the `translate` filter by the
            // region. Empty except while a slow operation is running.
            message: '',
        };

        state.begin = function (messageKey) {
            depth += 1;
            state.isBusy = true;

            // Only the OUTERMOST operation owns the message: reloadCart() runs
            // nested inside updateShipment(), and letting the inner one retitle
            // the wait makes a single customer action speak twice.
            //
            // An UNNAMED operation is one the customer did not start -- the cart
            // load on page init is the only one today -- so it neither speaks nor
            // blocks. Naming every operation instead would announce "Updating
            // your cart" on every arrival at /cart and hold the page inert while
            // it loaded.
            if (depth > 1 || !messageKey) {
                return;
            }

            state.blocked = true;
            state.stuck = false;

            pending = $timeout(function () {
                state.message = messageKey || '';
            }, ANNOUNCE_AFTER_MS);

            stuckTimer = $timeout(function () {
                state.blocked = false;
                state.stuck = true;
                state.message = 'checkout.busy.taking_longer';
            }, RELEASE_BLOCK_AFTER_MS);
        };

        state.end = function () {
            depth = Math.max(0, depth - 1);

            if (depth > 0) {
                return;
            }

            state.isBusy = false;
            state.blocked = false;
            state.stuck = false;
            $timeout.cancel(pending);
            $timeout.cancel(stuckTimer);
            pending = null;
            stuckTimer = null;
            // Cleared, not replaced with a completion phrase: the outcome is
            // already announced elsewhere -- role="alert" on the address error
            // (templates/cart.liquid:267) when it fails, and the step-heading
            // focus move (checkout/$checkout.js:106) when it succeeds. Emptying an
            // aria-atomic region announces nothing, which is what we want here.
            state.message = '';
        };

        $rootScope.rsBusy = state;

        return state;
    }])

    .service('customerService', ['$http', function ($http) {
        return {
            getCurrentCustomer() {
                return $http.get('storefrontapi/account?t=' + new Date().getTime());
            },
        };
    }])

    .service('marketingService', ['$http', function ($http) {
        return {
            getDynamicContent(placeName) {
                return $http.get('storefrontapi/marketing/dynamiccontent/' + placeName + '?t=' + new Date().getTime());
            },
        };
    }])

    .service('pricingService', ['$http', function ($http) {
        return {
            getActualProductPrices(products) {
                return $http.post('storefrontapi/pricing/actualprices', products);
            },
        };
    }])

    .service('compareProductService', ['$http', '$localStorage', function ($http, $localStorage) {
        return {
            isInProductCompareList(productId) {
                return $localStorage.productCompareListIds.some((id) => id === productId);
            },
            addProduct(productId) {
                if (!$localStorage.productCompareListIds) {
                    $localStorage.productCompareListIds = [];
                }

                $localStorage.productCompareListIds.push(productId);

                _.uniq($localStorage.productCompareListIds);
            },
            getProductsIds() {
                if (!$localStorage.productCompareListIds) {
                    $localStorage.productCompareListIds = [];
                    return '';
                }

                const ids = [];

                for (let i = 0; i < $localStorage.productCompareListIds.length; i += 1) {
                    ids.push('productIds=' + $localStorage.productCompareListIds[i]);
                }

                return ids.join('&');
            },
            getProductsCount() {
                return $localStorage.productCompareListIds ? $localStorage.productCompareListIds.length : 0;
            },
            clearComapreList() {
                $localStorage.productCompareListIds = [];
            },
            removeProduct(productId) {
                $localStorage.productCompareListIds = _.without($localStorage.productCompareListIds, productId);
            },
        };
    }])

    .service('catalogService', ['$http', function ($http) {
        return {
            getProduct(productIds) {
                return $http.get('storefrontapi/products?productIds=' + productIds + '&t=' + new Date().getTime());
            },
            getProducts(productIds) {
                return $http.get('storefrontapi/products?productIds='
                    + productIds.join('&productIds=') + '&t=' + new Date().getTime());
            },
            search(criteria) {
                return $http.post('storefrontapi/catalog/search', criteria);
            },
            searchCategories(criteria) {
                return $http.post('storefrontapi/categories/search', criteria);
            },
            searchAdditionalCartProducts() {
                return $http.post('storefrontapi/rs/catalog/cartadditionalproducts');
            },
        };
    }])

    .service('cartService', ['$http', '$q', '$timeout', function ($http, $q, $timeout) {
        let deferedCart = null;

        return {
            getCart(conf) {
                if (!deferedCart) {
                    deferedCart = $q.defer();

                    $timeout(function () {
                        deferedCart.resolve($http.get('storefrontapi/cart?t=' + new Date().getTime(), conf));
                        deferedCart = null;
                    }, 100);
                }

                return deferedCart.promise;
            },
            getCartItemsCount() {
                return $http.get('storefrontapi/cart/itemscount?t=' + new Date().getTime());
            },
            updateCardComment() {
                return $http.put('storefrontapi/cart/comment', {
                    comment: '',
                });
            },
            addLineItem(productId, quantity) {
                return $http.post('storefrontapi/cart/items', {
                    id: productId,
                    quantity,
                });
            },
            changeLineItemQuantity(lineItemId, quantity) {
                return $http.put('storefrontapi/cart/items', {
                    lineItemId,
                    quantity,
                });
            },
            removeLineItem(lineItemId) {
                return $http.delete('storefrontapi/cart/items?lineItemId=' + lineItemId);
            },
            changeLineItemPrice(lineItemId, newPrice) {
                return $http.put('storefrontapi/cart/items/price', {
                    lineItemId,
                    newPrice,
                });
            },
            clearCart() {
                return $http.post('storefrontapi/cart/clear');
            },
            getCountries() {
                return $http.get('storefrontapi/countries?t=' + new Date().getTime());
            },
            getCountryRegions(countryCode) {
                return $http.get('storefrontapi/countries/' + countryCode + '/regions?t=' + new Date().getTime());
            },
            addCoupon(couponCode) {
                return $http.post('storefrontapi/cart/coupons/' + couponCode);
            },
            removeCoupon(couponCode) {
                return $http.delete('storefrontapi/cart/coupons?couponCode=' + couponCode);
            },
            validateCoupon(coupon) {
                return $http.post('storefrontapi/cart/coupons/validate', coupon);
            },
            addOrUpdateShipment(shipment, calculateTax = false) {
                return $http.post('storefrontapi/rscart/shipments?tax=' + calculateTax, shipment);
            },
            addOrUpdatePayment(payment) {
                return $http.post('storefrontapi/rscart/payments', payment);
            },
            getAvailableShippingMethods(shipmentId) {
                return $http.get('storefrontapi/cart/shipments/' + shipmentId
                    + '/shippingmethods?t=' + new Date().getTime());
            },
            getAvailablePaymentMethods() {
                return $http.get('storefrontapi/cart/paymentmethods?t=' + new Date().getTime());
            },
            addOrUpdatePaymentPlan(plan) {
                return $http.post('storefrontapi/cart/paymentPlan', plan);
            },
            removePaymentPlan() {
                return $http.delete('storefrontapi/cart/paymentPlan');
            },
            createOrder(/* bankCardInfo */) {
                const request = { TimeZone: (new Date().getTimezoneOffset() / 60) * -1 };
                return $http.post('storefrontapi/cart/createorder', request);
            },
            updateCartComment(cartComment) {
                return $http.put('storefrontapi/cart/comment', {
                    comment: cartComment,
                });
            },
            getAssociatedProducts() {
                return $http.get('storefrontapi/rscart/cartassocproducts');
            },
            performApplePayMerchantValidation(request) {
                return $http.post('storefrontapi/rs/applepay/validatemerchant', request);
            },
            createTransactionFromToken(request) {
                return $http.post('storefrontapi/rs/applepay/createtransactionfromtoken', request);
            },
        };
    }])

    .service('listService', ['$http', function ($http) {
        const listsURL = 'storefrontapi/lists/';

        return {
            getWishlist(listName, type) {
                return $http.get(listsURL + listName + '/' + type + '?t=' + new Date().getTime());
            },
            getListsWithProduct(productId, listNames, type) {
                return $http.post('storefrontapi/lists/getlistswithproduct', {
                    productId,
                    listNames,
                    type,
                });
            },
            addLineItem(productId, listName, type) {
                return $http.post('storefrontapi/lists/items', {
                    productId,
                    listName,
                    type,
                });
            },
            removeLineItem(lineItemId, listName, type) {
                return $http.delete(listsURL + listName + '/' + type + '/items/' + lineItemId);
            },
            searchLists(searchCriteria) {
                return $http.post('storefrontapi/lists/search', searchCriteria);
            },
            createList(listName, type) {
                return $http.post(listsURL + listName + '/' + type + '/create');
            },
            deleteListsByIds(listIds) {
                return $http.delete('storefrontapi/lists/deletelistsbyids?listIds=' + listIds.join('&listIds='));
            },
            mergeWithCurrentCart(listName, type) {
                return $http.post(listsURL + listName + '/' + type + '/mergewithcurrentcart');
            },
        };
    }])

    .service('quoteRequestService', ['$http', function ($http) {
        const quoteRequestsURL = 'storefrontapi/quoterequests/';

        return {
            getCurrentQuoteRequest() {
                return $http.get('storefrontapi/quoterequest/current?t=' + new Date().getTime());
            },
            getQuoteRequest(number) {
                return $http.get(quoteRequestsURL + number + '?t=' + new Date().getTime());
            },
            getQuoteRequestItemsCount(number) {
                return $http.get(quoteRequestsURL + number + '/itemscount?t=' + new Date().getTime());
            },
            addProductToQuoteRequest(productId, quantity) {
                return $http.post('storefrontapi/quoterequests/current/items', {
                    productId,
                    quantity,
                });
            },
            removeProductFromQuoteRequest(quoteRequestNumber, quoteItemId) {
                return $http.delete(quoteRequestsURL + quoteRequestNumber + '/items/' + quoteItemId);
            },
            submitQuoteRequest(quoteRequestNumber, quoteRequest) {
                return $http.post(quoteRequestsURL + quoteRequestNumber + '/submit', quoteRequest);
            },
            rejectQuoteRequest(quoteRequestNumber) {
                return $http.post(quoteRequestsURL + quoteRequestNumber + '/reject');
            },
            updateQuoteRequest(quoteRequestNumber, quoteRequest) {
                return $http.put(quoteRequestsURL + quoteRequestNumber + '/update', quoteRequest);
            },
            getTotals(quoteRequestNumber, quoteRequest) {
                return $http.post(quoteRequestsURL + quoteRequestNumber + '/totals', quoteRequest);
            },
            confirmQuoteRequest(quoteRequestNumber, quoteRequest) {
                return $http.post(quoteRequestsURL + quoteRequestNumber + '/confirm', quoteRequest);
            },
        };
    }])

    .service('recommendationService', ['$http', function ($http) {
        return {
            getRecommendedProducts(requestData) {
                return $http.post('storefrontapi/recommendations', requestData);
            },
        };
    }])

    .service('orderService', ['$http', function ($http) {
        return {
            getOrder(orderNumber) {
                return $http.get('storefrontapi/orders/' + orderNumber + '?t=' + new Date().getTime());
            },
        };
    }])

    .service('rsCarouselService', ['$http', function ($http) {
        return {
            get(name) {
                return $http.get('storefrontapi/rs/marketing/dynamiccontent/' + name + '?t=' + new Date().getTime());
            },
        };
    }])

    .service('vcCustomerReviewsService', ['$http', function ($http) {
        return {
            addCustomerReview(customerReview) {
                return $http.post('storefrontapi/customerReviews/submit', customerReview);
            },
            search(criteria) {
                return $http.post('storefrontapi/customerReviews/search', criteria);
            },
            searchOwn(criteria) {
                return $http.post('storefrontapi/customerReviews/searchownreview', criteria);
            },
            checkIfSubmitAvailable(productId) {
                return $http.get('storefrontapi/customerReviews/checksubmit?productid=' + productId);
            },
        };
    }])

    .service('vcCustomerReviewsRatingService', ['$http', function ($http) {
        return {
            getRating(storeId, productId) {
                return $http.post('storefrontapi/rating/productRatingInStore', {
                    storeId,
                    productIds: [productId],
                });
            },
        };
    }])

    .service('rsCatalogService', ['$http', function ($http) {
        return {
            getColors() {
                return $http.get('storefrontapi/rs/catalog/colors', {});
            },
            getVariationsForProducts(productIds) {
                return $http.get('storefrontapi/rs/catalog/products?productIds=' + productIds.join('&productIds='), {});
            },
        };
    }])

    .service('rsSizingChartService', ['$http', function ($http) {
        return {
            getSizingChart(productId) {
                return $http.post('storefrontapi/rs/sizingchart/searchbyproduct', [productId]);
            },
        };
    }])

    .service('rsStoreLocatorService', ['$http', function ($http) {
        return {
            getStores() {
                // return $http.get('storelocator.json', {});
                return $http.get('storefrontapi/rs/storelocator/stores', {});
            },
        };
    }])

    .service('rsEmailSubscriptionService', ['$http', function ($http) {
        return {
            subscribe(model) {
                return $http.post('storefrontapi/rs/emailsubscription/subscribe', model);
            },
        };
    }])

    .service('rsAddressValidateService', ['$http', function ($http) {
        return {
            validate(address) {
                return $http.post('storefrontapi/rs/validateaddress', address);
            },
        };
    }])

    .service('rsAccountService', ['$http', function ($http) {
        return {
            resetPassword(model) {
                return $http.post('storefrontapi/rs/account/forgotpassword', model);
            },
        };
    }])

    .service('productListService', ['$q', 'rsCatalogService',
        function ($q, rsCatalogService) {
            return {
                getProductListData(productIds) {
                    const promise1 = rsCatalogService.getVariationsForProducts(productIds)
                        .then(function (response) {
                            return response.data;
                        });

                    const promise2 = rsCatalogService.getColors()
                        .then(function (response) {
                            return response.data;
                        });

                    return $q.all([promise1, promise2])
                        .then(function (result) {
                            return {
                                applyColorVariation(imageUrl) {
                                    return {
                                        'background-image': 'url(' + imageUrl + ')',
                                    };
                                },
                                variations: result[0],
                                colors: result[1],
                                variationsLoaded: true,
                                colorsLoaded: true,
                            };
                        });
                },
            };
        },
    ])

    .service('rsAddressCompareService', [function () {
        return {
            compare(adr1, adr2) {
                if (!adr1 || !adr2) {
                    return false;
                }

                return adr1.city === adr2.city
                    && adr1.countryCode === adr2.countryCode
                    && adr1.countryName === adr2.countryName
                    && adr1.line1 === adr2.line1
                    && adr1.line2 === adr2.line2
                    && adr1.regionId === adr2.regionId
                    && adr1.regionName === adr2.regionName
                    && adr1.postalCode === adr2.postalCode
                    && adr1.firstName === adr2.firstName
                    && adr1.lastName === adr2.lastName;
            },
            validate(address) {
                return address
                    && address.firstName
                    && address.lastName
                    && address.line1
                    && address.city
                    && address.countryCode
                    && address.regionId
                    && address.postalCode;
            },
        };
    }])

    .service('googleAnalyticsService', [function () {
        return {
            addProductToCart(product, quantity) {
                gtag('event', 'add_to_cart', {
                    value: product.price.actualPrice.amount * quantity,
                    items: [{
                        item_id: product.sku,
                        item_name: product.name,
                        item_category: product.categoryId,
                        quantity,
                        price: product.price.actualPrice.amount,
                    }],
                });
            },
            removeProductFromCart(lineItem) {
                gtag('event', 'remove_from_cart', {
                    value: lineItem.placedPrice.amount * lineItem.quantity,
                    items: [{
                        item_id: lineItem.sku,
                        item_name: lineItem.name,
                        item_category: lineItem.categoryId,
                        quantity: lineItem.quantity,
                        price: lineItem.placedPrice.amount,
                    }],
                });
            },
            addProductToWishlist(product) {
                gtag('event', 'add_to_wishlist', {
                    items: [{
                        item_id: product.sku,
                        item_name: product.name,
                        item_category: product.categoryId,
                        price: product.price.actualPrice.amount,
                    }],
                });
            },
            viewItem(product) {
                gtag('event', 'view_item', {
                    items: [{
                        item_id: product.sku,
                        item_name: product.name,
                        item_category: product.categoryId,
                        price: product.price.actualPrice.amount,
                    }],
                });
            },
            purchase(order) {
                gtag('event', 'purchase', {
                    transaction_id: order.number,
                    value: order.total.amount,
                    currency: order.currency.code,
                    tax: order.taxTotal.amount,
                    shipping: order.shippingTotal.amount,
                    items: _.map(order.items, function (item) {
                        return {
                            item_id: item.sku,
                            item_name: item.name,
                            item_category: item.categoryId,
                            price: item.placedPrice.amount,
                            quantity: item.quantity,
                        };
                    }),
                });
            },
            beginCheckout(products, coupon) {
                gtag('event', 'begin_checkout', {
                    coupon,
                    value: _.reduce(products, function (num, product) { return product.placedPrice.amount + num; }, 0),
                    // eslint-disable-next-line sonarjs/no-identical-functions
                    items: _.map(products, function (item) {
                        return {
                            item_id: item.sku,
                            item_name: item.name,
                            item_category: item.categoryId,
                            price: item.placedPrice.amount,
                            quantity: item.quantity,
                        };
                    }),
                });
            },
            addPaymentInfo() {
                gtag('event', 'add_payment_info');
            },
        };
    }]);
